import type { ReactNode } from "react";

/** A section title inside a panel or sheet. */
export const SectionHeading = ({
  children,
  action,
  className = "",
}: {
  children: ReactNode;
  /** A small control on the right, such as "Add page". */
  action?: ReactNode;
  className?: string;
}) => (
  <div className={`mb-3 flex items-baseline justify-between gap-3 ${className}`}>
    <h3 className="font-lora text-lg font-semibold text-hazumu-ink">{children}</h3>
    {action}
  </div>
);

/** Muted helper text under a control or section. */
export const Hint = ({
  children,
  className = "",
}: {
  children: ReactNode;
  className?: string;
}) => <p className={`text-xs leading-5 text-hazumu-muted ${className}`}>{children}</p>;

const cards = {
  panel: "rounded-2xl border border-hazumu-border bg-hazumu-warm-white p-4",
  hero: "rounded-3xl border border-hazumu-border bg-hazumu-warm-white p-6 shadow-xl shadow-hazumu-clay/20 sm:p-10",
} as const;

export const Card = ({
  variant = "panel",
  className = "",
  children,
}: {
  variant?: keyof typeof cards;
  className?: string;
  children: ReactNode;
}) => <section className={`${cards[variant]} ${className}`}>{children}</section>;

/** A full-screen page that centres one message or form. */
export const CenteredPage = ({ children }: { children: ReactNode }) => (
  <main className="flex min-h-screen items-center justify-center bg-hazumu-canvas px-5 py-8 text-hazumu-ink">
    {children}
  </main>
);

/** An invitation shown where a list has nothing in it yet. */
export const EmptyState = ({
  icon,
  title,
  children,
  action,
}: {
  icon: ReactNode;
  title: string;
  children?: ReactNode;
  action?: ReactNode;
}) => (
  <div className="flex flex-col items-center gap-2 rounded-2xl border border-dashed border-hazumu-clay px-6 py-8 text-center">
    <span
      aria-hidden="true"
      className="mb-1 grid size-11 place-items-center rounded-full bg-hazumu-blush text-hazumu-terracotta"
    >
      {icon}
    </span>
    <p className="font-lora text-base font-semibold text-hazumu-ink">{title}</p>
    {children && <p className="max-w-64 text-xs leading-5 text-hazumu-muted">{children}</p>}
    {action && <div className="mt-2">{action}</div>}
  </div>
);
